import { Key } from 'react';
import { IFilters, ThemeList, IFiltersLei } from '../globalTypes';

export interface IGlobalState {
  error: boolean | null;
  loading: boolean;
  scheme: ThemeList;
  searchParams: string;
  tableFilters: ITableFilters;
  appliedFiltersResult: any;
  filtersData: IFilters[];
  selectedOption99: string;
  superAdmin: boolean;
  filtersDataLei: IFiltersLei[];
}

export interface ITitleBarProps {
  title: any;
}

export interface ISelectedOptions {
  [key: string]: string[];
}

export interface ITableFilters {
  [key: string]: Key[];
}

export interface Step {
  key?: keyof typeof STEP_KEYS;
  title: string;
  content?: React.ReactNode;
}

export interface StepComponentProps {
  steps: Step[];
  title: string;
  children?: React.ReactNode;
  footer?: JSX.Element;
  onAddRow?: (record?: any, index?: number) => void;
  onStepChange?: (step: number) => void;
  current?: number;
}

export interface ICTSystem extends DataTypeCustomTable {
  key: number;
  id: string;
  label: string;
  value: string;
  description: string;
  start_date: string;
  end_date: string;
  isNew?: boolean;
  isEdited?: boolean;
}

export type CardNodeData = {
  title: string;
  href: string;
};

export interface SidebarProps {
  open: boolean;
}

export interface AreaTitleProps {
  isShort?: boolean;
}

export interface OverlayProps {
  show: boolean;
}

export interface StyledCardProps {
  token: any;
  minHeight?: string;
}

export interface SectionProps {
  section: string;
  token?: any;
}

export interface GoBackButtonProps {
  to?: string;
  text: string;
  goBack?: boolean;
}

export type HierarchicalTreeProps = {
  title: string;
  data: any[];
  entityOptions: { value: number; label: string }[];
  selectedEntityId: number | null;
  setSelectedEntityId: (v: number) => void;
  onDateChange: (dateString: string) => void;
  buildTree: (data: any[]) => any;
};

export type AllocationConfig = {
  id: string;
  title: string;
  type:
    | 'entity'
    | 'branch'
    | 'function'
    | 'tpsp'
    | 'contractualArrangements'
    | 'ictSystems'
    | 'ictServices';
  hasParent: boolean;
  parentOptions: { value: number; label: string }[];
  dependsOn?: string;
  parentSource?:
    | 'entity'
    | 'financial'
    | 'tpsp'
    | 'contractualArrangements'
    | 'contracts';
};

export type InfoType = {
  function_location_of_data_at_rest_name?: string;
  function_location_of_data_management_name?: string;
  function_data_site?: string;
  function_sensitiveness_of_data_stored_by_ict_tpsp_name?: string;

  ict_service_location_of_data_at_rest_name?: string;
  ict_service_location_of_data_management_name?: string;
  ict_service_data_site?: string;
  ict_service_sensitiveness_of_data_stored_by_ict_tpsp_name?: string;

  interactive?: boolean;
};

export interface AllocationItem {
  key: string;
  title: string;
  [key: string]: any;
}

export interface TargetConfig {
  targets: Record<string, Key[]>;
  dataSource: Record<string, AllocationItem[]>;
}

export interface UseLoadAllocationProps {
  onBranchLoad: (data: AllocationItem[]) => void;
  onFunctionLoad: (data: AllocationItem[]) => void;
  onTpspLoad: (data: AllocationItem[]) => void;
  onContractualArrangementsLoad: (data: AllocationItem[]) => void;
  onIctSystemsLoad?: (data: AllocationItem[]) => void;
  onIctServicesLoad?: (data: AllocationItem[]) => void;
}

export interface UseSaveAllocationProps {
  onSetLoading: (id: string, loading: boolean) => void;
}

export interface SaveConfig {
  id: string;
  parent: { id: number; label: string } | undefined;
  onSave: () => Promise<void>;
  onReload: (parentId: number) => Promise<void>;
  successMessage: string;
}

export interface SaveParams {
  id: string;
  parent: { id: number; label: string };
  selectedItems: any[];
  dataSource: any[];
  successMessage: string;
}

export interface TransferConfig {
  dataSource: any[];
  targetKeys: Key[];
  onChange: (keys: Key[]) => void;
}

export interface TransferConfigMap {
  entity: TransferConfig;
  branch: TransferConfig;
  function: TransferConfig;
  tpsp: TransferConfig;
  contractualArrangements: TransferConfig;
  ictSystems?: TransferConfig;
  ictServices?: TransferConfig;
}

export interface AllocationConfigItem {
  fetchFn: () => Promise<any>;
  saveFn: (payload: Record<string, any>) => Promise<any>;
  fetchByEntityFn: (id: number) => Promise<any>;
  parentKey: string;
  dataKey: string;
  entityNameKey: string;
  buildPayload?: (
    items: Record<string, any>[],
    parent: { id: number; label: string },
  ) => Record<string, any>;
}

export type InstructionItem =
  | {
      type: 'text';
      label: string;
      value: string;
    }
  | {
      type: 'group';
      label: string;
      items: string[];
    };

export interface DataTypeCustomTable {
  [key: string]: any;
  key: number;
  active?: boolean;
}

export interface EditableRowProps {
  index: number;
}

export interface EditableCellProps {
  title: React.ReactNode;
  editable: boolean;
  children: React.ReactNode;
  dataIndex: keyof DataTypeCustomTable;
  record: DataTypeCustomTable;
  handleSave: (record: DataTypeCustomTable) => void;
}

export interface DynamicFieldProps {
  value: string | null;
  fieldName: string;
  rowKey: string | number;
  onChange: (
    e: React.ChangeEvent<HTMLInputElement>,
    rowKey: string | number,
    fieldName: string,
  ) => void;
  type?: 'input' | 'date';
  format?: string;
  useDefaultDate?: boolean;
}

export const STEP_KEYS = {
  ENTITY_MAINTAINING_REGISTER: 'ENTITY_MAINTAINING_REGISTER',
  ENTITY: 'ENTITY',
  ENTITY_TYPE: 'ENTITY_TYPE',
  BRANCH: 'BRANCH',
  FUNCTION: 'FUNCTION',
  ICT_SYSTEMS: 'ICT_SYSTEMS',
  DATA: 'DATA',
  ICT_PROVIDER: 'ICT_PROVIDER',
  CURRENCY_AND_COSTS: 'CURRENCY_AND_COSTS',
  CONTRACTUAL_INFORMATION: 'CONTRACTUAL_INFORMATION',
  ICT_SERVICE: 'ICT_SERVICE',
  ICT_RISK: 'ICT_RISK',
  REPORT99: 'REPORT99',
  ENTITY_ROLE: 'ENTITY_ROLE',
  RESPONSIBILITIES: 'RESPONSIBILITIES',
  ICT_RISK_EXPOSURE: 'ICT_RISK_EXPOSURE',
  ROLE_TYPE: 'ROLE_TYPE',
  ICT_RISK_SOURCE: 'ICT_RISK_SOURCE',
  ICT_RISK_LEVEL: 'ICT_RISK_LEVEL',
  EXPOSURE_CATEGORY: 'EXPOSURE_CATEGORY',
  DEPENDENCY_TYPE: 'DEPENDENCY_TYPE',
  ICT_SYSTEM_CRITICALITY: 'ICT_SYSTEM_CRITICALITY',
} as const;
